import React, { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

// Components
import { useSelector } from 'react-redux';
import LeftSideBarCourseManagement from '../../containers/LeftSideBar/LeftSideBarCourseManagement';
import BreadcumbsFilter from '../../containers/BreadcumbsFilter';
import ShadowScrollbar from '../Scrollbar/ShadowScrollbars';
import SectionDetail from '../../containers/CourseManagementSection/SectionDetail';
import TeacherList from '../../containers/CourseManagementSection/TeacherList';
import StudentList from '../../containers/CourseManagementSection/StudentList';
import MainContent from '../../containers/MainContent';

// Styles
import {
  PrimaryWrap,
  DefaultContentWrap,
  ContentSubFlexWrap,
} from '../Generals/stylesheets/General.style';
import { CourseManagementFlexWrap } from './CourseManagementSection.style';

// Types
import { ICourse } from '../../types/course';
import { ISection } from '../../types/section';
import { IUser } from '../../types/user';

// Constants
const SCROLL_STYLE = {
  height: 'calc(100vh - 5.5em)',
  width: '100%',
} as const;

const SIDEBAR_PATH = '/management/course' as const;

// Interfaces
interface CourseManagementSectionProps {
  sectionsStudentGet: (sectionId: string) => void;
  sectionsTeachersGet: (sectionId: string) => void;
  sectionsEstimatedGet: (sectionId: string) => void;
  sectionDetail: ISection;
  studentList: IUser[];
  teacherList: IUser[];
  courseDetail: ICourse;
  isSideBarClose: boolean;
  isGettingStudentList: boolean;
  isGettingTeacherList: boolean;
}

interface RouteParams {
  sectionId: string;
  courseId: string;
}

// Custom hooks for separation of concerns (Single Responsibility Principle)
const useSectionDataFetching = (
  sectionId: string,
  courseDetail: ICourse,
  sectionsStudentGet: (sectionId: string) => void,
  sectionsTeachersGet: (sectionId: string) => void,
  sectionsEstimatedGet: (sectionId: string) => void,
) => {
  useEffect(() => {
    if (!sectionId) {
      return;
    }

    sectionsStudentGet(sectionId);
    sectionsTeachersGet(sectionId);

    if (courseDetail.has_exam) {
      sectionsEstimatedGet(sectionId);
    }
  }, [
    sectionId,
    courseDetail.has_exam,
    sectionsStudentGet,
    sectionsTeachersGet,
    sectionsEstimatedGet,
  ]);
};

const useSectionIdChange = (
  sectionId: string,
  courseDetail: ICourse,
  sectionsStudentGet: (sectionId: string) => void,
  sectionsTeachersGet: (sectionId: string) => void,
  sectionsEstimatedGet: (sectionId: string) => void,
) => {
  const teacherList = useSelector(
    (state: { sectionTeacher: { teachers: { [key: string]: any } } }) =>
      state.sectionTeacher?.teachers?.[sectionId],
  );
  useEffect(() => {
    if (teacherList === undefined) {
      sectionsStudentGet(sectionId);
      sectionsTeachersGet(sectionId);

      if (courseDetail.has_exam) {
        sectionsEstimatedGet(sectionId);
      }
    }
  }, [
    sectionId,
    courseDetail.has_exam,
    sectionsStudentGet,
    sectionsTeachersGet,
    sectionsEstimatedGet,
    teacherList,
  ]);
};

const useNavigation = (sectionDetail: ISection, courseDetail: ICourse) => {
  const history = useHistory();

  useEffect(() => {
    if (!sectionDetail || Object.keys(sectionDetail).length === 0) {
      history.push(`/management/course/${courseDetail.id}`);
    }
  }, [sectionDetail, courseDetail.id, history]);
};

// Memoized components for performance optimization
const CourseManagementBreadcrumbs = React.memo<{
  courseDetail: ICourse;
  sectionDetail: ISection;
  isSideBarClose: boolean;
}>(({ courseDetail, sectionDetail, isSideBarClose }) => (
  <BreadcumbsFilter
    haveSub={isSideBarClose}
    breadcumbSubList={[courseDetail.name]}
    breadcumbList={[courseDetail.name, sectionDetail.name, 'Manage']}
    mobileTitle="Sections Management"
    isDisplayFilter={false}
  />
));

CourseManagementBreadcrumbs.displayName = 'CourseManagementBreadcrumbs';

const CourseManagementContent = React.memo<{
  sectionDetail: ISection;
  studentList: IUser[];
  teacherList: IUser[];
  courseId: string;
  isGettingStudentList: boolean;
  isGettingTeacherList: boolean;
}>(
  ({
    sectionDetail,
    studentList,
    teacherList,
    courseId,
    isGettingStudentList,
    isGettingTeacherList,
  }) => (
    <CourseManagementFlexWrap>
      <SectionDetail sectionDetail={sectionDetail} studentList={studentList} />
      <ShadowScrollbar autoHide style={SCROLL_STYLE}>
        <ContentSubFlexWrap>
          <TeacherList
            teacherList={teacherList}
            sectionDetail={sectionDetail}
            courseId={courseId}
            isGettingTeacherList={isGettingTeacherList}
          />
          <StudentList
            studentList={studentList}
            sectionDetail={sectionDetail}
            isGettingStudentList={isGettingStudentList}
          />
        </ContentSubFlexWrap>
      </ShadowScrollbar>
    </CourseManagementFlexWrap>
  ),
);

CourseManagementContent.displayName = 'CourseManagementContent';

// Main component
const CourseManagementSection: React.FC<CourseManagementSectionProps> = ({
  sectionsStudentGet,
  sectionsTeachersGet,
  sectionsEstimatedGet,
  sectionDetail,
  studentList,
  teacherList,
  courseDetail,
  isSideBarClose,
  isGettingStudentList,
  isGettingTeacherList,
}) => {
  const { sectionId, courseId } = useParams<RouteParams>();

  // Custom hooks for data fetching and navigation
  useSectionDataFetching(
    sectionId,
    courseDetail,
    sectionsStudentGet,
    sectionsTeachersGet,
    sectionsEstimatedGet,
  );

  useSectionIdChange(
    sectionId,
    courseDetail,
    sectionsStudentGet,
    sectionsTeachersGet,
    sectionsEstimatedGet,
  );

  useNavigation(sectionDetail, courseDetail);
  // Early return if section detail is empty (handled by useNavigation hook)
  if (!sectionDetail || Object.keys(sectionDetail).length === 0) {
    return null;
  }

  return (
    <PrimaryWrap>
      <LeftSideBarCourseManagement pathName={SIDEBAR_PATH} />
      <MainContent>
        <CourseManagementBreadcrumbs
          courseDetail={courseDetail}
          sectionDetail={sectionDetail}
          isSideBarClose={isSideBarClose}
        />

        <DefaultContentWrap>
          <ShadowScrollbar autoHide style={SCROLL_STYLE}>
            <CourseManagementContent
              sectionDetail={sectionDetail}
              studentList={studentList}
              teacherList={teacherList}
              courseId={courseId}
              isGettingStudentList={isGettingStudentList}
              isGettingTeacherList={isGettingTeacherList}
            />
          </ShadowScrollbar>
        </DefaultContentWrap>
      </MainContent>
    </PrimaryWrap>
  );
};

export default CourseManagementSection;
